import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, MapPin, Sparkles, Upload,
  FileText, ShieldAlert, X, Send, Trash2,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STORAGE_KEY = "dwig_portal_session_id";
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const AGENT_MSG = {
  4: (n) => `Hi ${n || "there"}! I'm Agent 1, your intake concierge. Let's start with a few details about you — your legal name, current address, and marital status.`,
  5: (n) => `Great work, ${n || "friend"}. Now tell me how you make a living so I know which documents to ask for next.`,
  6: (n) => `Almost there. Give me your target purchase price and ZIP code and I'll check county loan limits in real time.`,
  docs: (n, hint) => `Perfect. Based on your income details, I need a few documents to move your file forward. ${hint || ""} Drag them into the slots on the right — Agent 2 will parse them instantly, and Agent 3 will silently check integrity.`,
};

const STEP_LABELS = { 4: "Getting to know you", 5: "Employment", 6: "Purchase Intent", docs: "Documents" };

export default function Portal() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [session, setSession] = useState(null);
  const [countyInfo, setCountyInfo] = useState(null);
  const [slots, setSlots] = useState({ slots: [], uploads: {}, income_type: "w2" });
  const [chatLog, setChatLog] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [phase, setPhase] = useState("intro");   // intro | form | docs | complete
  const chatEnd = useRef(null);
  // Tracks which user_id has already had a bootstrap started; survives StrictMode double-invoke.
  const bootstrappedFor = useRef(null);

  // Auto-load an existing session (or create one for an authenticated applicant)
  // so the redundant "Create secure session" screen never blocks an already-signed-in user.
  useEffect(() => {
    if (!user || !user.email || !user.user_id) return;
    if (bootstrappedFor.current === user.user_id) return;
    bootstrappedFor.current = user.user_id;

    (async () => {
      try {
        const sid = localStorage.getItem(STORAGE_KEY);
        if (sid) {
          try {
            const r = await api.get(`/portal/session/${sid}`);
            // Only adopt a stored session if it belongs to THIS user
            if ((r.data.email || "").toLowerCase() === user.email.toLowerCase()) {
              setSession(r.data);
              setPhase(r.data.completed ? "docs" : "form");
              return;
            }
            localStorage.removeItem(STORAGE_KEY);
          } catch {
            localStorage.removeItem(STORAGE_KEY);
          }
        }

        // Create a fresh session
        const created = await api.post("/portal/session", {
          email: user.email,
          phone: user.phone || "",
          program: params.get("program") || "conventional",
          source_slug: params.get("source") || "",
        });
        localStorage.setItem(STORAGE_KEY, created.data.id);

        // Prefill demographics name from the account name (single-token → first name only)
        let sessionToUse = created.data;
        if (user.name && user.name.trim()) {
          const tokens = user.name.trim().split(/\s+/);
          const first = tokens.shift() || "";
          const last = tokens.length ? tokens.pop() : "";
          const middle = tokens.join(" ");
          try {
            const patched = await api.patch(`/portal/session/${created.data.id}`, {
              step: 4,
              demographics: { legal_first_name: first, legal_middle_name: middle, legal_last_name: last },
            });
            sessionToUse = patched.data;
          } catch { /* keep created session even if patch fails */ }
        }
        setSession(sessionToUse);
        setPhase("form");
      } catch (err) {
        // Release the guard so a retry (e.g. via reload) is possible
        bootstrappedFor.current = null;
        toast.error(err?.response?.data?.detail || "Could not start your file.");
      }
    })();
  }, [user?.user_id, user?.email, user?.name, user?.phone, params]);

  useEffect(() => {
    if (!session || phase !== "docs") return;
    api.get(`/portal/session/${session.id}/slots`).then((r) => setSlots(r.data));
  }, [session, phase]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chatLog]);

  // Push agent messages as step advances
  useEffect(() => {
    if (!session) return;
    const name = session.demographics?.legal_first_name;
    let key = phase === "docs" ? "docs" : String(session.step);
    if (phase === "form" && chatLog.length === 0) {
      setChatLog([{ from: "agent", text: AGENT_MSG[key](name) }]);
    }
    if (phase === "docs" && !chatLog.some((m) => m.text?.startsWith("Perfect."))) {
      const hint = slots.income_type === "w2"
        ? "For W-2 earners like you: photo ID, two paystubs, most recent W-2, and 2 months of bank statements."
        : "";
      setChatLog((c) => [...c, { from: "agent", text: AGENT_MSG.docs(name, hint) }]);
    }
  }, [phase, session, slots.income_type]);

  const save = async (step, patch) => {
    if (!session) return null;
    const r = await api.patch(`/portal/session/${session.id}`, { step, ...patch });
    setSession(r.data);
    return r.data;
  };

  const goto = async (step) => {
    if (session) await save(step, {});
    if (chatLog[chatLog.length - 1]?.text !== AGENT_MSG[step](session?.demographics?.legal_first_name)) {
      setChatLog((c) => [...c, { from: "agent", text: AGENT_MSG[step](session?.demographics?.legal_first_name) }]);
    }
  };

  const finishForm = async () => {
    await save(6, { completed: true });
    setPhase("docs");
  };

  const signOut = async () => {
    localStorage.removeItem(STORAGE_KEY);
    await logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const zip = session?.intent?.target_zip;
    if (!zip || zip.length < 3 || session.step !== 6) { setCountyInfo(null); return; }
    let alive = true;
    api.get(`/portal/loan-limit?zip=${zip}&program=${session.program || "conventional"}`)
      .then((r) => alive && setCountyInfo(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [session?.intent?.target_zip, session?.step, session?.program]);

  const uploadFile = async (slotKey, file) => {
    if (!session) return;
    const fd = new FormData();
    fd.append("slot", slotKey);
    fd.append("file", file);
    // Optimistic parsing indicator
    setSlots((s) => ({
      ...s,
      uploads: { ...s.uploads, [slotKey]: [...(s.uploads[slotKey] || []), { pending: true, original_filename: file.name }] },
    }));
    try {
      const url = `${API}/portal/session/${session.id}/upload`;
      const res = await fetch(url, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const rec = await res.json();
      // Reload full slots snapshot
      const r = await api.get(`/portal/session/${session.id}/slots`);
      setSlots(r.data);
      if (rec.status === "flagged") {
        toast.error(rec.fraud_reason || "File flagged by fraud agent.");
        setChatLog((c) => [...c, { from: "agent", text: `Agent 3 flagged your upload. ${rec.fraud_reason}` }]);
      } else {
        const ext = rec.extracted || {};
        const detail = ext.gross_pay ? `Gross ${fmt(ext.gross_pay)} · YTD ${fmt(ext.ytd_gross)}` : "Parsed successfully.";
        setChatLog((c) => [...c, { from: "agent", text: `${rec.original_filename} verified. ${detail}` }]);
      }
    } catch {
      toast.error("Upload failed.");
      // Rollback optimistic
      const r = await api.get(`/portal/session/${session.id}/slots`);
      setSlots(r.data);
    }
  };

  const deleteUpload = async (uploadId) => {
    await api.delete(`/portal/session/${session.id}/upload/${uploadId}`);
    const r = await api.get(`/portal/session/${session.id}/slots`);
    setSlots(r.data);
  };

  const sendChat = () => {
    if (!chatInput.trim()) return;
    setChatLog((c) => [...c, { from: "user", text: chatInput }, { from: "agent", text: "Thanks — I'll pass that along to your loan originator. Please continue with the documents on the right." }]);
    setChatInput("");
  };

  const progressPct = useMemo(() => {
    if (!session) return 0;
    if (phase === "complete") return 100;
    if (phase === "docs") return 85;
    return ((session.step - 3) / 3) * 60;
  }, [session, phase]);

  const requiredSlotsFilled = useMemo(() => {
    const req = slots.slots.filter((s) => s.required);
    return req.every((s) => (slots.uploads[s.key] || []).some((u) => u.status === "verified"));
  }, [slots]);

  if (!session) {
    // ProtectedRoute guarantees `user` is loaded before we render; the auto-create
    // effect below spins up a session. Show a lightweight loader instead of the
    // redundant "Create secure session" form.
    return (
      <div className="min-h-screen flex flex-col bg-white" data-testid="page-portal">
        <BrandStripe thickness="h-1.5" />
        <main className="flex-1 grid place-items-center">
          <div className="text-center">
            <div className="w-14 h-14 grid place-items-center bg-[#00A651]/10 text-[#00A651] rounded-sm mx-auto">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">Applicant Portal</p>
            <h1 className="font-heading font-bold text-3xl text-black mt-2">Opening your file…</h1>
            <p className="mt-2 text-gray-500 font-sub italic">Agent 1 is preparing your intake.</p>
          </div>
        </main>
        <BrandStripe thickness="h-1.5" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F7FB]" data-testid="page-portal">
      <BrandStripe thickness="h-1.5" />
      <header className="border-b border-gray-200 sticky top-0 bg-white z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo size={36} /></Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-xs uppercase tracking-wider font-bold text-gray-600">
              {session.program?.toUpperCase()} Application Hub
              {session.intent?.target_zip && <span className="text-gray-400"> · {session.intent.target_zip}</span>}
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500">Progress</span>
              <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#00A651]" animate={{ width: `${progressPct}%` }} transition={{ duration: 0.4 }} />
              </div>
            </div>
            <button onClick={signOut} className="text-xs uppercase tracking-wider font-bold text-gray-500 hover:text-[#ED1C24]" data-testid="portal-signout">Sign out</button>
            <Link to="/schedule" data-testid="portal-book-call" className="hidden sm:inline-flex text-[11px] uppercase tracking-wider font-bold text-[#00A651] border border-[#00A651]/40 hover:bg-[#00A651]/10 px-2.5 py-1 rounded transition">
              Book a call
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 grid lg:grid-cols-12 gap-6">
        {/* LEFT — chat + form */}
        <section className="lg:col-span-5 bg-white border border-gray-100 rounded-2xl shadow-sm flex flex-col h-[calc(100vh-10rem)]">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-lg bg-[#00A651]/10 text-[#00A651]"><Sparkles size={18} /></span>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#00A651]">Agent 1 · Intake Concierge</p>
                <p className="font-heading font-bold text-sm text-black">{STEP_LABELS[phase === "docs" ? "docs" : session.step]}</p>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {chatLog.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`max-w-[85%] ${m.from === "user" ? "ml-auto bg-black text-white" : "bg-[#F6F7FB] text-black"} p-3 rounded-2xl text-sm`}>
                {m.text}
              </motion.div>
            ))}
            {phase === "form" && (
              <div className="pt-3">
                <AnimatePresence mode="wait">
                  <motion.div key={session.step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                    {session.step === 4 && <DemographicsForm session={session} onSave={save} onNext={() => goto(5)} />}
                    {session.step === 5 && <EmploymentForm session={session} onSave={save} onBack={() => goto(4)} onNext={() => goto(6)} />}
                    {session.step === 6 && <IntentForm session={session} onSave={save} countyInfo={countyInfo}
                      onBack={() => goto(5)} onFinish={finishForm} />}
                  </motion.div>
                </AnimatePresence>
              </div>
            )}
            <div ref={chatEnd} />
          </div>
          <div className="p-3 border-t border-gray-100 flex items-center gap-2">
            <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendChat()}
              data-testid="portal-chat-input" placeholder="Type a message…"
              className="flex-1 bg-[#F6F7FB] border-transparent focus:border-[#00A651] focus:bg-white outline-none rounded-full px-4 py-2 text-sm" />
            <button onClick={sendChat} data-testid="portal-chat-send" className="w-9 h-9 grid place-items-center rounded-full bg-[#00A651] text-white hover:bg-[#008f45]"><Send size={14} /></button>
          </div>
        </section>

        {/* RIGHT — dynamic slots */}
        <section className="lg:col-span-7 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 h-[calc(100vh-10rem)] overflow-y-auto">
          {phase === "form" && <FormSummaryPanel session={session} countyInfo={countyInfo} />}
          {phase === "docs" && (
            <DocSlots slots={slots} onUpload={uploadFile} onDelete={deleteUpload}
              requiredFilled={requiredSlotsFilled} onSubmit={() => setPhase("complete")} />
          )}
          {phase === "complete" && <CompleteView session={session} slots={slots} />}
        </section>
      </main>
      <BrandStripe thickness="h-1.5" />
    </div>
  );
}

/* ---- Slot uploads ---- */
function DocSlots({ slots, onUpload, onDelete, requiredFilled, onSubmit }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">Dynamic Upload Slots</p>
          <h2 className="font-heading font-bold text-2xl text-black mt-1">Documents ({slots.income_type?.toUpperCase()})</h2>
        </div>
        <button onClick={onSubmit} disabled={!requiredFilled} data-testid="docs-submit"
          className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-40 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg">
          Submit File <ArrowRight size={14} />
        </button>
      </div>
      <div className="mt-5 space-y-3">
        {slots.slots.map((s) => (
          <SlotRow key={s.key} slot={s} uploads={slots.uploads[s.key] || []} onUpload={onUpload} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function SlotRow({ slot, uploads, onUpload, onDelete }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const verified = uploads.some((u) => u.status === "verified");
  const flagged = uploads.some((u) => u.status === "flagged");
  const pending = uploads.some((u) => u.pending);
  const c = verified ? "#00A651" : flagged ? "#ED1C24" : (pending ? "#E88C2F" : "#9CA3AF");
  return (
    <div className="border rounded-xl p-4" style={{ borderColor: c }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onUpload(slot.key, f);
      }}
      data-testid={`slot-${slot.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 grid place-items-center rounded-lg" style={{ backgroundColor: `${c}15`, color: c }}>
            <FileText size={16} />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm text-black truncate">{slot.label}</p>
            <p className="text-[10px] text-gray-500">{slot.hint}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {verified && <span className="text-[10px] uppercase tracking-wider font-bold text-[#00A651] flex items-center gap-1"><CheckCircle2 size={12} /> Verified</span>}
          {pending && !verified && !flagged && <span className="text-[10px] uppercase tracking-wider font-bold text-[#E88C2F] flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> Processing</span>}
          {flagged && <span className="text-[10px] uppercase tracking-wider font-bold text-[#ED1C24] flex items-center gap-1"><ShieldAlert size={12} /> Flagged</span>}
        </div>
      </div>
      <div className={`mt-3 border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${drag ? "border-[#00A651] bg-[#00A651]/5" : "border-gray-200 hover:border-black"}`}
        onClick={() => inputRef.current?.click()}>
        <Upload className="mx-auto text-gray-400" size={18} />
        <p className="text-xs text-gray-500 mt-2">Drag &amp; drop or click to browse</p>
        <input ref={inputRef} type="file" hidden onChange={(e) => e.target.files?.[0] && onUpload(slot.key, e.target.files[0])} data-testid={`slot-input-${slot.key}`} />
      </div>
      {uploads.length > 0 && (
        <ul className="mt-3 space-y-2">
          {uploads.map((u, i) => (
            <li key={u.id || i} className="flex items-center justify-between text-xs bg-[#F6F7FB] px-3 py-2 rounded-lg">
              <div className="min-w-0">
                <p className="font-heading font-bold text-black truncate">{u.original_filename}</p>
                {u.status === "verified" && u.extracted?.gross_pay && (
                  <p className="text-[10px] text-gray-500">Gross {fmt(u.extracted.gross_pay)} · YTD {fmt(u.extracted.ytd_gross)}</p>
                )}
                {u.status === "flagged" && <p className="text-[10px] text-[#ED1C24]">{u.fraud_reason}</p>}
                {u.pending && <p className="text-[10px] text-[#E88C2F]">Parsing document structures…</p>}
              </div>
              {u.id && (
                <button onClick={() => onDelete(u.id)} className="text-gray-400 hover:text-[#ED1C24]" data-testid={`slot-del-${u.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---- Form panels (compact, live in the right column while step 4-6 active) ---- */
function FormSummaryPanel({ session, countyInfo }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651]">File Snapshot</p>
      <h2 className="font-heading font-bold text-2xl text-black mt-1">Your intake so far</h2>
      <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
        <Snap label="Name" v={`${session.demographics?.legal_first_name || "—"} ${session.demographics?.legal_last_name || ""}`.trim()} />
        <Snap label="ZIP" v={session.demographics?.residential_zip || "—"} />
        <Snap label="Income Type" v={session.employment?.income_type?.toUpperCase() || "—"} />
        <Snap label="Annual Income" v={session.employment?.annual_gross_income ? fmt(session.employment.annual_gross_income) : "—"} />
        <Snap label="Target Price" v={session.intent?.target_purchase_price ? fmt(session.intent.target_purchase_price) : "—"} />
        <Snap label="Target Program" v={(session.intent?.target_program || session.program || "").toUpperCase()} />
      </div>
      {countyInfo && (
        <div className="mt-6 bg-[#F6F7FB] rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#00A651] flex items-center gap-1"><MapPin size={12} /> County limit ({countyInfo.zip})</p>
          <p className="font-heading font-bold text-2xl mt-1">{fmt(countyInfo.conventional_limit)}</p>
          <p className="text-xs text-gray-500 mt-1">FHA {fmt(countyInfo.fha_limit)} · {countyInfo.high_cost_area ? "High-cost area" : "Standard area"}</p>
        </div>
      )}
    </div>
  );
}
function Snap({ label, v }) {
  return (
    <div className="border-l-2 border-[#00A651] pl-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</p>
      <p className="font-heading font-bold text-sm text-black truncate">{v}</p>
    </div>
  );
}

/* ---- Forms (compact versions of prior step components) ---- */
function DemographicsForm({ session, onSave, onNext }) {
  const [d, setD] = useState(session.demographics || {});
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const ok = d.legal_first_name && d.legal_last_name && d.residential_zip;
  return (
    <div data-testid="portal-step-4" className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <F label="First" v={d.legal_first_name} on={(v) => set("legal_first_name", v)} t="dem-first" />
        <F label="Last" v={d.legal_last_name} on={(v) => set("legal_last_name", v)} t="dem-last" />
      </div>
      <F label="Current Address" v={d.residential_address} on={(v) => set("residential_address", v)} t="dem-addr" />
      <div className="grid grid-cols-3 gap-2">
        <F label="City" v={d.residential_city} on={(v) => set("residential_city", v)} t="dem-city" />
        <F label="State" v={d.residential_state} on={(v) => set("residential_state", v)} t="dem-state" />
        <F label="ZIP" v={d.residential_zip} on={(v) => set("residential_zip", v)} t="dem-zip" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <SF label="Marital" v={d.marital_status} on={(v) => set("marital_status", v)} t="dem-marital"
          opts={[["single","Single"],["married","Married"],["separated","Separated"],["unmarried","Unmarried"]]} />
        <SF label="Housing" v={d.housing_status} on={(v) => set("housing_status", v)} t="dem-housing"
          opts={[["own","Own"],["rent","Rent"],["other","Other"]]} />
      </div>
      <button onClick={async () => { await onSave(4, { demographics: d }); onNext(); }} disabled={!ok} data-testid="dem-next"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-40 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg">
        Continue <ArrowRight size={14} />
      </button>
    </div>
  );
}
function EmploymentForm({ session, onSave, onBack, onNext }) {
  const [d, setD] = useState(session.employment || {});
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const ok = d.income_type && d.annual_gross_income > 0;
  const isSE = d.income_type === "self_employed" || d.income_type === "entity_investor";
  return (
    <div data-testid="portal-step-5" className="space-y-3">
      <SF label="Income Type" v={d.income_type} on={(v) => set("income_type", v)} t="emp-type"
        opts={[["w2","W-2 Employee"],["self_employed","Self-Employed"],["1099","1099"],["entity_investor","Entity Investor"]]} />
      {!isSE ? (
        <div className="grid grid-cols-2 gap-2">
          <F label="Employer" v={d.employer_name} on={(v) => set("employer_name", v)} t="emp-employer" />
          <F label="Job Title" v={d.job_title} on={(v) => set("job_title", v)} t="emp-title" />
        </div>
      ) : (
        <F label="Business Name" v={d.business_name} on={(v) => set("business_name", v)} t="emp-business" />
      )}
      <div className="grid grid-cols-2 gap-2">
        <F label="Yrs Employed" type="number" v={d.years_employed} on={(v) => set("years_employed", Number(v))} t="emp-years" />
        <F label="Annual Gross" type="number" v={d.annual_gross_income} on={(v) => set("annual_gross_income", Number(v))} t="emp-income" />
      </div>
      <div className="flex gap-2">
        <button onClick={onBack} className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-gray-300 rounded-lg" data-testid="emp-back">Back</button>
        <button onClick={async () => { await onSave(5, { employment: d }); onNext(); }} disabled={!ok} data-testid="emp-next"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-40 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg">
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
function IntentForm({ session, onSave, onBack, onFinish }) {
  const [d, setD] = useState({ target_program: session.program || "conventional", ...(session.intent || {}) });
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const ok = Number(d.target_purchase_price) > 0 && d.target_zip;
  return (
    <div data-testid="portal-step-6" className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <F label="Target Price" type="number" v={d.target_purchase_price} on={(v) => set("target_purchase_price", Number(v))} t="intent-price" />
        <F label="Down Payment" type="number" v={d.expected_down_payment} on={(v) => set("expected_down_payment", Number(v))} t="intent-down" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <F label="Target ZIP" v={d.target_zip} on={(v) => set("target_zip", v)} t="intent-zip" />
        <SF label="Program" v={d.target_program} on={(v) => set("target_program", v)} t="intent-program"
          opts={[["conventional","Conv"],["fha","FHA"],["va","VA"],["usda","USDA"],["jumbo","Jumbo"],["refinance","Refi"]]} />
      </div>
      <div className="flex gap-2">
        <button onClick={onBack} className="px-3 py-2 text-xs uppercase tracking-wider font-bold border border-gray-300 rounded-lg" data-testid="intent-back">Back</button>
        <button onClick={async () => { await onSave(6, { intent: d }); onFinish(); }} disabled={!ok} data-testid="intent-finish"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-40 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg">
          Continue to Documents <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function CompleteView({ session, slots }) {
  const uploaded = Object.values(slots.uploads).flat().filter((u) => u.status === "verified").length;
  return (
    <div className="text-center py-10" data-testid="portal-complete">
      <CheckCircle2 className="text-[#00A651] mx-auto" size={64} />
      <h1 className="font-heading font-bold text-3xl text-black mt-6">File submitted to Agent 7.</h1>
      <p className="font-sub italic text-gray-600 mt-3 max-w-md mx-auto">
        {uploaded} document{uploaded === 1 ? "" : "s"} verified. Your loan originator will review the package
        and reach out with next steps.
      </p>
      <p className="mt-4 text-xs text-gray-500 font-mono">Session ID: {session.id}</p>
    </div>
  );
}

function F({ label, v, on, t, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-bold">{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={t} className="mt-1 rounded-lg h-9" />
    </div>
  );
}
function SF({ label, v, on, opts, t }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-bold">{label}</Label>
      <Select value={v ?? ""} onValueChange={on}>
        <SelectTrigger data-testid={t} className="mt-1 rounded-lg h-9"><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>{opts.map(([vv, ll]) => <SelectItem key={vv} value={vv}>{ll}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
